/** Small readers of score configs and answers, shared by the queue and experiment pages. */

import type { ScoreConfig } from "./types";

/** "Pass / fail", "1–5 scale", "3 categories". */
export function kindLabel(c: ScoreConfig): string {
  if (c.kind === "pass_fail") return "Pass / fail";
  if (c.kind === "scale") return `${c.scale_min}–${c.scale_max} scale`;
  return `${c.categories.length} categories`;
}

/** What a reviewer can answer, in one line. */
export function allowedAnswers(c: ScoreConfig): string {
  if (c.kind === "pass_fail") return "Pass, Fail";
  if (c.kind === "scale") {
    const [low, high] = c.scale_labels;
    const ends = low || high ? ` (${low || c.scale_min} → ${high || c.scale_max})` : "";
    return `${c.scale_min} to ${c.scale_max}${ends}`;
  }
  return c.categories.join(", ");
}

/** An answer as people read it: "Pass", "4 / 5", the category. */
export function answerLabel(c: ScoreConfig, value: string): string {
  if (c.kind === "pass_fail") return value === "pass" ? "Pass" : "Fail";
  if (c.kind === "scale") return `${value} / ${c.scale_max}`;
  return value;
}

/** Two letters for an avatar: "priya.nair@qure.ai" → "PN". */
export function initials(email: string): string {
  const local = email.split("@")[0] ?? email;
  const parts = local.split(/[._-]+/).filter(Boolean);
  const letters = parts.length > 1 ? parts[0]![0]! + parts[1]![0]! : local.slice(0, 2);
  return letters.toUpperCase();
}

/** Emails typed as "a@x.ai, b@x.ai" or one per line. */
export const splitEmails = (text: string) =>
  text
    .split(/[\s,;]+/)
    .map((e) => e.trim().toLowerCase())
    .filter(Boolean);

/** Equal as JSON: the corrected answer against the agent's. */
export const sameValue = (a: unknown, b: unknown) => JSON.stringify(a) === JSON.stringify(b);

/** A JSON object (not a list, not null). */
export const isObject = (v: unknown): v is Record<string, unknown> =>
  typeof v === "object" && v !== null && !Array.isArray(v);
